import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, BookOpen, Brain, Loader2, Sparkles, AlertCircle, BookText } from 'lucide-react'
import { articlesApi } from '@/api/articles'
import { Button } from '@/components/ui/Button'
import { useAiJobPoller, isPracticeResult } from '@/hooks/useAiJobPoller'
import { cn } from '@/components/ui/cn'
import { copy } from '@/lib/copy'
import { AI_CREDIT_COSTS } from '@/lib/aiCredits'
import { useAiCredits } from '@/hooks/useAiCredits'
import { AiCreditCostBadge } from '@/components/ai/AiCreditsIndicator'
import { containsSuspiciousText, inputLimits, trimToLimit } from '@/lib/inputLimits'

export interface ComprehensionQuestion {
  question: string
  type: string
}

type Confidence = 'guess' | 'not_sure' | 'pretty_sure' | 'absolutely_sure'
type Verdict = 'correct' | 'partial' | 'needs_support' | 'skipped'
type Closeness = 'correct' | 'very_close' | 'partially_related' | 'unrelated'
type FlowPhase = 'question' | 'feedback' | 'summary'
type FeedbackReveal = 'hint' | 'full'

export interface QuestionFeedback {
  verdict: Verdict
  closeness: Closeness
  feedbackHeading: string
  aboutYourAnswer: string
  hintPrompt?: string
  contextQuote?: string
  articleContext?: string
  phraseInContext?: string
  whyThisWord?: string
  strengthNote?: string
}

interface CheckResponse {
  questions: Array<QuestionFeedback & { index: number }>
  recommendations: string[]
}

const CONFIDENCE_OPTIONS: { value: Confidence; label: string }[] = [
  { value: 'guess', label: copy.comprehension.confidence.guess },
  { value: 'not_sure', label: copy.comprehension.confidence.not_sure },
  { value: 'pretty_sure', label: copy.comprehension.confidence.pretty_sure },
  { value: 'absolutely_sure', label: copy.comprehension.confidence.absolutely_sure },
]

function questionTypeLabel(type: string) {
  const key = type.toLowerCase() as keyof typeof copy.comprehension.types
  return copy.comprehension.types[key] ?? type
}

function normalizeVerdict(value: string): Verdict {
  if (value === 'correct' || value === 'partial' || value === 'skipped') return value
  return 'needs_support'
}

function normalizeCloseness(value: string | undefined, verdict: Verdict): Closeness {
  if (value === 'very_close' || value === 'partially_related' || value === 'unrelated' || value === 'correct') {
    return value
  }
  if (verdict === 'correct') return 'correct'
  if (verdict === 'partial') return 'partially_related'
  return 'unrelated'
}

export function parseComprehensionQuestions(raw: string): ComprehensionQuestion[] {
  try {
    const start = raw.indexOf('[')
    const end = raw.lastIndexOf(']')
    if (start !== -1 && end !== -1 && end > start) {
      return JSON.parse(raw.slice(start, end + 1))
    }
  } catch {
    // fall through
  }
  return []
}

function parseCheckResponse(raw: string): CheckResponse | null {
  try {
    const start = raw.indexOf('{')
    const end = raw.lastIndexOf('}')
    if (start === -1 || end === -1 || end <= start) return null

    const parsed = JSON.parse(raw.slice(start, end + 1)) as {
      questions?: Array<{
        index?: number
        verdict?: string
        closeness?: string
        feedbackHeading?: string
        aboutYourAnswer?: string
        feedback?: string
        hintPrompt?: string
        contextQuote?: string
        articleQuote?: string
        articleContext?: string
        phraseInContext?: string
        whyThisWord?: string
        strengthNote?: string
      }>
      recommendations?: string[]
    }

    if (!Array.isArray(parsed.questions) || parsed.questions.length === 0) return null

    return {
      questions: parsed.questions.map((item, index) => {
        const verdict = normalizeVerdict(item.verdict ?? 'needs_support')
        return {
          index: item.index ?? index,
          verdict,
          closeness: normalizeCloseness(item.closeness, verdict),
          feedbackHeading: item.feedbackHeading ?? 'Key idea:',
          aboutYourAnswer: item.aboutYourAnswer ?? item.feedback ?? '',
          hintPrompt: item.hintPrompt,
          contextQuote: item.contextQuote || item.articleQuote,
          articleContext: item.articleContext,
          phraseInContext: item.phraseInContext,
          whyThisWord: item.whyThisWord,
          strengthNote: item.strengthNote,
        }
      }),
      recommendations: Array.isArray(parsed.recommendations) ? parsed.recommendations : [],
    }
  } catch {
    return null
  }
}

function extractVocabularyWord(question: string) {
  const match = question.match(/['"]([^'"]+)['"]/)
  return match?.[1]
}

function needsGuidedReveal(feedback: QuestionFeedback, questionType: string) {
  if (questionType === 'personal') return false
  if (feedback.verdict === 'correct') return false
  return Boolean(feedback.contextQuote || feedback.hintPrompt)
}

interface ComprehensionFlowProps {
  articleId: string
  content: string
  onRegenerate: () => void
}

export function ComprehensionFlow({ articleId, content, onRegenerate }: ComprehensionFlowProps) {
  const { recordCharge } = useAiCredits()
  const questions = parseComprehensionQuestions(content)

  const [phase, setPhase] = useState<FlowPhase>('question')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [answer, setAnswer] = useState('')
  const [confidence, setConfidence] = useState<Confidence | null>(null)
  const [feedbacks, setFeedbacks] = useState<QuestionFeedback[]>([])
  const [recommendations, setRecommendations] = useState<string[]>([])
  const [checkJobId, setCheckJobId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [feedbackReveal, setFeedbackReveal] = useState<FeedbackReveal>('hint')

  const currentQuestion = questions[currentIndex]
  const currentFeedback = feedbacks[currentIndex]
  const isLastQuestion = currentIndex >= questions.length - 1

  useAiJobPoller({
    jobId: checkJobId,
    onDone: (response) => {
      if (!isPracticeResult(response.result)) {
        setError('Something went wrong. Please try again.')
        setIsSubmitting(false)
        setCheckJobId(null)
        return
      }

      const parsed = parseCheckResponse(response.result.content)
      if (!parsed?.questions[0]) {
        setError('Could not read feedback. Please try again.')
        setIsSubmitting(false)
        setCheckJobId(null)
        return
      }

      const item = parsed.questions[0]
      setFeedbacks((prev) => {
        const next = [...prev]
        next[currentIndex] = item
        return next
      })

      if (parsed.recommendations.length > 0) {
        setRecommendations((prev) => [...new Set([...prev, ...parsed.recommendations])])
      }

      setFeedbackReveal(needsGuidedReveal(item, currentQuestion?.type ?? '') ? 'hint' : 'full')
      setPhase('feedback')
      setIsSubmitting(false)
      setCheckJobId(null)
    },
    onError: (msg) => {
      setError(msg)
      setIsSubmitting(false)
      setCheckJobId(null)
    },
  })

  const answerLimit = inputLimits.comprehensionAnswer
  const trimmedAnswer = answer.trim()
  const answerCharsLeft = answerLimit - answer.length
  const answerTooLong = trimmedAnswer.length > answerLimit
  const answerSuspicious = trimmedAnswer.length > 0 && containsSuspiciousText(trimmedAnswer)

  const canSubmit =
    trimmedAnswer.length > 0 &&
    confidence !== null &&
    !isSubmitting &&
    !answerTooLong &&
    !answerSuspicious

  const handleAnswerChange = (value: string) => {
    setAnswer(trimToLimit(value, answerLimit))
    if (error) setError(null)
  }

  const handleShareAnswer = async () => {
    if (!currentQuestion || !canSubmit) {
      if (answerTooLong) {
        setError(copy.comprehension.answerTooLong(answerLimit))
      } else if (answerSuspicious) {
        setError(copy.comprehension.answerInvalid)
      }
      return
    }

    try {
      setIsSubmitting(true)
      setError(null)
      const res = await articlesApi.checkComprehension(articleId, [
        {
          question: currentQuestion.question,
          type: currentQuestion.type,
          answer: answer.trim(),
          confidence: confidence ?? undefined,
        },
      ])
      await recordCharge(AI_CREDIT_COSTS.comprehensionCheck)
      setCheckJobId(res.data.jobId)
    } catch {
      setError('Could not reach your coach right now. Please try again.')
      setIsSubmitting(false)
    }
  }

  const handleNext = () => {
    if (isLastQuestion) {
      setPhase('summary')
      return
    }
    setCurrentIndex((value) => value + 1)
    setAnswer('')
    setConfidence(null)
    setFeedbackReveal('hint')
    setPhase('question')
  }

  const handleTryAgain = () => {
    setAnswer('')
    setConfidence(null)
    setFeedbackReveal('hint')
    setPhase('question')
  }

  const handleSkipForNow = () => {
    setFeedbacks((prev) => {
      const next = [...prev]
      next[currentIndex] = {
        verdict: 'skipped',
        closeness: 'unrelated',
        feedbackHeading: copy.comprehension.skippedHeading,
        aboutYourAnswer: copy.comprehension.skippedNote,
      }
      return next
    })
    handleNext()
  }

  const handleRestart = () => {
    setPhase('question')
    setCurrentIndex(0)
    setAnswer('')
    setConfidence(null)
    setFeedbacks([])
    setRecommendations([])
    setFeedbackReveal('hint')
    setError(null)
  }

  if (questions.length === 0) {
    return (
      <div className="space-y-2">
        <pre className="whitespace-pre-wrap text-sm leading-7">{content}</pre>
        <button onClick={onRegenerate} className="text-xs text-muted-foreground hover:text-foreground">
          Regenerate
        </button>
      </div>
    )
  }

  if (phase === 'summary') {
    return (
      <ReadingSummary
        questions={questions}
        feedbacks={feedbacks}
        recommendations={recommendations}
        onRestart={handleRestart}
        onRegenerate={onRegenerate}
      />
    )
  }

  const progress = ((currentIndex + (phase === 'feedback' ? 1 : 0)) / questions.length) * 100

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{copy.comprehension.questionOf(currentIndex + 1, questions.length)}</span>
          <span className="capitalize">{questionTypeLabel(currentQuestion.type)}</span>
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {phase === 'question' ? (
        <div className="space-y-5">
          <p className="text-base font-medium leading-relaxed text-foreground">{currentQuestion.question}</p>

          <textarea
            value={answer}
            onChange={(e) => handleAnswerChange(e.target.value)}
            placeholder={copy.comprehension.answerPlaceholder}
            rows={3}
            maxLength={answerLimit}
            disabled={isSubmitting}
            className="w-full resize-none rounded-lg border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
            autoFocus
          />
          <p
            className={cn(
              'text-right text-xs',
              answerCharsLeft < 100 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
            )}
          >
            {copy.comprehension.charactersLeft(Math.max(0, answerCharsLeft))}
          </p>

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">{copy.comprehension.confidenceLabel}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {CONFIDENCE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setConfidence(option.value)}
                  className={cn(
                    'rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                    confidence === option.value
                      ? 'border-primary bg-primary/10 text-foreground ring-1 ring-primary/20'
                      : 'border-border bg-card text-foreground hover:border-primary/40 hover:bg-secondary/40'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {error ? (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          {isSubmitting ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              {copy.actions.thinkingWithYou}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button className="h-9 w-auto gap-2 px-4 text-sm" disabled={!canSubmit} onClick={handleShareAnswer}>
              {copy.actions.shareAnswer}
              <AiCreditCostBadge cost={AI_CREDIT_COSTS.comprehensionCheck} />
              <ArrowRight className="h-4 w-4" />
            </Button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={handleSkipForNow}
              data-testid="comprehension-skip-for-now"
              className="inline-flex h-9 items-center rounded-md px-4 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
            >
              {copy.comprehension.skipForNow}
            </button>
            <button
              onClick={onRegenerate}
              disabled={isSubmitting}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              New questions
              <AiCreditCostBadge cost={AI_CREDIT_COSTS.comprehensionGenerate} />
            </button>
          </div>
        </div>
      ) : null}

      {phase === 'feedback' && currentFeedback ? (
        <FeedbackCard
          feedback={currentFeedback}
          questionType={currentQuestion.type}
          reveal={feedbackReveal}
          isLast={isLastQuestion}
          onRevealFull={() => setFeedbackReveal('full')}
          onTryAgain={handleTryAgain}
          onNext={handleNext}
        />
      ) : null}
    </div>
  )
}

function FeedbackCard({
  feedback,
  questionType,
  reveal,
  isLast,
  onRevealFull,
  onTryAgain,
  onNext,
}: {
  feedback: QuestionFeedback
  questionType: string
  reveal: FeedbackReveal
  isLast: boolean
  onRevealFull: () => void
  onTryAgain: () => void
  onNext: () => void
}) {
  const showGuidedFlow = needsGuidedReveal(feedback, questionType)
  const showHintPhase = showGuidedFlow && reveal === 'hint'
  const showFullExplanation = !showGuidedFlow || reveal === 'full'

  return (
    <div className="space-y-5">
      <div
        className={cn(
          'rounded-xl border p-4',
          feedback.verdict === 'correct' && 'border-primary/25 bg-primary/5',
          feedback.verdict !== 'correct' && 'border-border bg-secondary/15'
        )}
      >
        <p className="text-sm font-semibold text-foreground">{feedback.feedbackHeading}</p>

        <div className="mt-4 space-y-4">
          <FeedbackSection
            icon={<Brain className="h-4 w-4 text-primary" />}
            title={copy.comprehension.aboutYourAnswer}
          >
            <p className="text-sm leading-relaxed text-foreground">{feedback.aboutYourAnswer}</p>
          </FeedbackSection>

          {showHintPhase && feedback.hintPrompt ? (
            <div className="space-y-3">
              <p className="text-sm leading-relaxed text-muted-foreground">{feedback.hintPrompt}</p>
              {feedback.contextQuote ? (
                <div className="rounded-lg border border-primary/20 bg-background/80 px-4 py-3">
                  <p className="text-xs font-medium text-muted-foreground">{copy.comprehension.takeAnotherLook}</p>
                  <p className="mt-2 text-sm italic leading-relaxed text-foreground">
                    &ldquo;{feedback.contextQuote}&rdquo;
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}

          {showFullExplanation && feedback.articleContext ? (
            <FeedbackSection
              icon={<BookText className="h-4 w-4 text-primary" />}
              title={copy.comprehension.aboutTheArticle}
            >
              {feedback.phraseInContext ? (
                <p className="mb-2 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">&ldquo;{feedback.phraseInContext}&rdquo;</span>
                </p>
              ) : null}
              <p className="text-sm leading-relaxed text-foreground">{feedback.articleContext}</p>
            </FeedbackSection>
          ) : null}

          {showFullExplanation && feedback.whyThisWord ? (
            <FeedbackSection
              icon={<Sparkles className="h-4 w-4 text-primary" />}
              title={copy.comprehension.whyThisWord}
            >
              <p className="text-sm leading-relaxed text-muted-foreground">{feedback.whyThisWord}</p>
            </FeedbackSection>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {showHintPhase ? (
          <>
            <Button className="h-9 w-auto px-4 text-sm" onClick={onTryAgain}>
              {copy.comprehension.tryAgain}
            </Button>
            <Button
              className="h-9 w-auto bg-secondary px-4 text-sm text-secondary-foreground hover:bg-secondary/80"
              onClick={onRevealFull}
            >
              {copy.comprehension.showExplanation}
            </Button>
            <button
              type="button"
              onClick={onNext}
              data-testid="comprehension-next-question"
              className="inline-flex h-9 items-center gap-2 rounded-md px-4 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {isLast ? copy.actions.seeSummary : copy.actions.nextQuestion}
              <ArrowRight className="h-4 w-4" />
            </button>
          </>
        ) : feedback.verdict !== 'correct' ? (
          <>
            <Button className="h-9 w-auto px-4 text-sm" onClick={onTryAgain}>
              {copy.comprehension.tryAgain}
            </Button>
            <Button className="h-9 w-auto gap-2 px-4 text-sm" onClick={onNext} data-testid="comprehension-next-question">
              {isLast ? copy.actions.seeSummary : copy.actions.nextQuestion}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <Button className="h-9 w-auto gap-2 px-4 text-sm" onClick={onNext} data-testid="comprehension-next-question">
            {isLast ? copy.actions.seeSummary : copy.actions.nextQuestion}
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}

function FeedbackSection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode
  title: string
  children: ReactNode
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {icon}
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      </div>
      {children}
    </div>
  )
}

function ReadingSummary({
  questions,
  feedbacks,
  recommendations,
  onRestart,
  onRegenerate,
}: {
  questions: ComprehensionQuestion[]
  feedbacks: QuestionFeedback[]
  recommendations: string[]
  onRestart: () => void
  onRegenerate: () => void
}) {
  const strengths = feedbacks
    .map((item) => item.strengthNote)
    .filter((item): item is string => Boolean(item?.trim()))

  const skippedCount = feedbacks.filter((item) => item.verdict === 'skipped').length

  const wordsToRevisit = questions
    .map((question, index) => {
      if (question.type !== 'vocabulary') return null
      const feedback = feedbacks[index]
      if (!feedback || feedback.verdict === 'correct' || feedback.verdict === 'skipped') return null
      return extractVocabularyWord(question.question)
    })
    .filter((word): word is string => Boolean(word))

  const mostlyCorrect = feedbacks.filter((item) => item.verdict === 'correct').length >= Math.ceil(questions.length / 2)

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
        <p className="text-sm font-semibold text-foreground">{copy.comprehension.readingSummary}</p>
        <p className="mt-2 text-sm leading-relaxed text-foreground">
          {mostlyCorrect ? copy.comprehension.greatClose : copy.comprehension.warmClose}
        </p>
        {skippedCount > 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            {copy.comprehension.skippedInSummary(skippedCount, questions.length)}
          </p>
        ) : null}
      </div>

      {strengths.length > 0 ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">{copy.comprehension.understoodWell}</p>
          <ul className="space-y-1.5">
            {strengths.map((item, index) => (
              <li key={index} className="flex gap-2 text-sm text-muted-foreground">
                <span className="text-primary">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {wordsToRevisit.length > 0 ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">{copy.comprehension.wordsToRevisit}</p>
          <ul className="space-y-1.5">
            {wordsToRevisit.map((word) => (
              <li key={word} className="flex gap-2 text-sm text-muted-foreground">
                <span className="text-amber-500">⚠</span>
                <span>
                  <span className="font-medium text-foreground">{word}</span> — worth another look while reading
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {recommendations.length > 0 ? (
        <div className="rounded-lg border bg-secondary/20 p-4">
          <p className="text-sm font-semibold text-foreground">{copy.comprehension.nextStep}</p>
          <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
            {recommendations.map((item, index) => (
              <li key={index} className="flex gap-2">
                <span className="text-primary">→</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <Link
          to="/reading"
          className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <BookOpen className="h-4 w-4" />
          {copy.comprehension.readyAnother}
        </Link>
        <Button
          className="h-9 w-auto bg-secondary px-4 text-sm text-secondary-foreground hover:bg-secondary/80"
          onClick={onRestart}
        >
          {copy.actions.tryAgainAnswers}
        </Button>
        <button onClick={onRegenerate} className="text-xs text-muted-foreground hover:text-foreground">
          New questions
        </button>
      </div>
    </div>
  )
}
